import { ProjectActions, ProjectSelectors } from "@app/project/store";
import { ApiResultState, LoadingState } from "@app/root-store/api-result.state";
import { ClareStatus, onlineClareStatus, unknownClareStatus } from "@app/shared/model/clare-status.model";
import { Store, select } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { catchError, distinctUntilKeyChanged, filter, map, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { ProjectState, ProjectStatusItem } from "../store/projects.state";

import { Injectable } from "@angular/core";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { ProjectStatus } from "../store/selectors/projects.selectors";

@Injectable({ providedIn: "root" })
export class ProjectStatusService {
    constructor(private store: Store<ProjectState>) {}

    /**
     * Get the online/offline statuses of project controllers
     *
     * @description Get the projectStatus state. Get the project controller status state
     * by getting the array of uuids from the project state. Once we get the array of UUIDs,
     * if the statuses have not been initialized, request them from RALink and return the original state.
     */
    public getProjectStatuses$(): Observable<ApiResultState<ClareDbCollection<ProjectStatusItem>>> {
        // Get the current state
        return this.selectProjectStatusesState$().pipe(
            // Combine it with the latest list of project UUIDs
            withLatestFrom(this.store.pipe(select(ProjectSelectors.getProjectUuids), distinctUntilKeyChanged("length"))),
            // If in the initial state, get the project statuses from RALink
            tap(([statusState, uuids]) => {
                if (statusState.callState === LoadingState.init && uuids?.length) {
                    this.store.dispatch(new ProjectActions.UpdateProjectStatuses({ uuids }));
                }
            }),
            // Return just the state
            map(([state]) => state),
            catchError(_ => of({ result: {}, callState: LoadingState.loaded }))
        );
    }

    /**
     * Get the project statuses state
     *
     * @description Get the project statuses state using the project statuses state
     */
    private selectProjectStatusesState$(): Observable<ApiResultState<ClareDbCollection<ProjectStatusItem>>> {
        return this.store.pipe(select(ProjectSelectors.getProjectStatusesState));
    }

    /**
     * Get project status counts
     *
     * @description Get the total number of online, offline and unconfigured project controllers
     * @example response { online: number, offline: number, unconfigured: number }
     */
    public selectProjectStatusCounts$(): Observable<Record<ProjectStatus, number>> {
        return this.store.pipe(select(ProjectSelectors.getProjectStatusCounts));
    }

    /**
     * Get active project status counts
     *
     * @description Get the total number of online, offline and unconfigured project controllers for active projects
     * @example response { online: number, offline: number, unconfigured: number }
     * @returns {Observable<Record<string, number>>} An observable with the counts of active project statuses
     */
    public selectActiveProjectStatusCounts$(): Observable<Record<ProjectStatus, number>> {
        return this.store.pipe(select(ProjectSelectors.getActiveProjectStatusCounts));
    }

    /**
     * Get project status counts
     *
     * @description Get the total number of online, offline and unconfigured project controllers
     * @example response { online: number, offline: number, unconfigured: number }
     */
    public selectProjectActiveCounts$(): Observable<Record<string, number>> {
        return this.store.pipe(select(ProjectSelectors.getProjectActiveCounts));
    }

    /**
     * Get project service plans counts
     *
     * @description Get the total number of basic, interactive, interactive +, interactive + LTE project controllers
     */
    public selectProjectServicePlansCounts$(): Observable<Record<string, number>> {
        return this.store.pipe(select(ProjectSelectors.getProjectServicePlanCount()));
    }

    /**
     * Get active project service plans counts
     *
     * @description Get the total number of basic, interactive, interactive +, interactive + LTE project controllers
     * @returns {Observable<Record<string, number>>} An observable with the counts of service plans
     */
    public selectActiveProjectServicePlansCounts$(): Observable<Record<string, number>> {
        return this.store.pipe(select(ProjectSelectors.getActiveProjectServicePlanCount()));
    }

    /**
     * Get project occupancy counts
     *
     * @description Get the total number of occupied/unoccupied controllers
     */
    public selectProjectOccupancyCounts$(): Observable<Record<string, number>> {
        return this.store.pipe(select(ProjectSelectors.selectProjectOccupancyCounts()));
    }

    /**
     * Get active project occupancy counts
     *
     * @description Get the total number of active occupied/unoccupied controllers
     * @returns {Observable<Record<string, number>>} An observable with the counts of active project occupancy
     */
    public selectActiveProjectOccupancyCounts$(): Observable<Record<string, number>> {
        return this.store.pipe(select(ProjectSelectors.selectActiveProjectOccupancyCounts()));
    }

    /**
     * Get project lockout counts
     *
     * @description Get the total number of locked out controllers
     */
    public selectProjectLockoutCount$(): Observable<number> {
        return this.store.pipe(select(ProjectSelectors.selectProjectLockoutCount()));
    }

    /**
     * Get active project lockout counts
     *
     * @description Get the total number of active locked out controllers
     * @returns {Observable<number>} An observable with the count of active locked out controllers
     */
    public selectActiveProjectLockoutCount$(): Observable<number> {
        return this.store.pipe(select(ProjectSelectors.selectActiveProjectLockoutCount()));
    }

    /**
     * Get a project's status
     *
     * @param projectId The ID of the project for which status to get
     * @description Get a project's status. Get the project statuses and then
     * select the specific project's status. If there is no project ID, return an unknown status.
     */
    public getProjectStatus$(projectId: number): Observable<ClareStatus> {
        if (projectId) {
            return this.getProjectStatuses$().pipe(
                filter(result => result.callState !== LoadingState.init && result.callState !== LoadingState.loading),
                switchMap(_ => this.store.pipe(select(ProjectSelectors.getProjectStatus(projectId))))
            );
        }
        return of(unknownClareStatus);
    }

    /**
     * Get the active project's status
     *
     * @param noDispatchWhenEmpty
     * @description Get the active project's online status by selecting the active project status state.
     * If the active project status is not populated, get the current UUID, then call RALink to get the status
     */
    public getActiveProjectStatus$(noDispatchWhenEmpty = false): Observable<ClareStatus> {
        return this.getActiveProjectStatusState$(noDispatchWhenEmpty).pipe(
            filter(([state]) => state.callState !== LoadingState.init && state.callState !== LoadingState.loading),
            map(([state]) => state.result)
        );
    }

    /**
     * Get the active project's status state
     *
     * @param noDispatchWhenEmpty
     * @description get active project status state
     */
    public getActiveProjectStatusState$(noDispatchWhenEmpty = false): Observable<[ApiResultState<ClareStatus>, string]> {
        return this.store.pipe(select(ProjectSelectors.getActiveProjectStatusState)).pipe(
            distinctUntilKeyChanged("callState"),
            withLatestFrom(this.store.pipe(select(ProjectSelectors.getUuid))),
            filter(([_, uuid]) => !!uuid),
            tap(([statusState, uuid]) => {
                if (statusState.callState === LoadingState.init && !noDispatchWhenEmpty) {
                    this.store.dispatch(new ProjectActions.GetActiveProjectStatus({ uuid }));
                }
            })
        );
    }

    public isActiveProjectOnline$(): Observable<boolean> {
        return this.getActiveProjectStatus$().pipe(map(status => onlineClareStatus === status));
    }
}
